import { TStyleCustomization } from '@components/FileUpload/components/dropzone/types/TStyleCustomization';
import React from 'react';

export const formatBytes = (b: number) => {
  const units = ['bytes', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let l = 0;
  let n = b;

  while (n >= 1024) {
    n /= 1024;
    l += 1;
  }

  return `${n.toFixed(n >= 10 || l < 1 ? 0 : 1)}${units[l]}`;
};

export const formatDuration = (seconds: number) => {
  const date = new Date(0);
  date.setSeconds(seconds);
  const dateString = date.toISOString().slice(11, 19);
  if (seconds < 3600) {
    return dateString.slice(3);
  }
  return dateString;
};

// adapted from: https://github.com/okonet/attr-accept/blob/master/src/index.js
// returns true if file.name is empty and accept string is something like ".csv",
// because file comes from dataTransferItem for drag events, and
// dataTransferItem.name is always empty
export const accepts = (file: File, accept: string) => {
  if (!accept || accept === '*') {
    return true;
  }

  const mimeType = file.type || '';
  const baseMimeType = mimeType.replace(/\/.*$/, '');

  return accept
    .split(',')
    .map((t) => t.trim())
    .some((type) => {
      if (type.charAt(0) === '.') {
        return file.name === undefined || file.name.toLowerCase().endsWith(type.toLowerCase());
      } else if (type.endsWith('/*')) {
        // this is something like an image/* mime type
        return baseMimeType === type.replace(/\/.*$/, '');
      }
      return mimeType === type;
    });
};

type ResolveFn<T> = (...args: any[]) => T;

export const resolveValue = <T>(value: ResolveFn<T> | T, ...args: unknown[]) => {
  if (typeof value === 'function') {
    return (value as ResolveFn<T>)(...args);
  }
  return value;
};

export const defaultClassNames = {
  dropzone:
    'relative mx-auto box-border flex min-h-[120px] w-full flex-col items-center overflow-auto border-2 border-[#d9d9d9] transition-all duration-150 ease-linear',
  dropzoneActive: 'border-[#2484ff] bg-[#deebff]',
  dropzoneReject: 'border-[#2484ff] bg-[#deebff]',
  dropzoneDisabled: 'opacity-50 [&_*:hover]:cursor-unset',
  input: 'hidden',
  inputLabel:
    'absolute inset-0 flex cursor-pointer items-center justify-center font-[Helvetica,sans-serif] text-xl font-semibold text-[#2484ff] antialiased',
  inputLabelWithFiles:
    'ml-[3%] mt-5 flex min-h-8 cursor-pointer items-center justify-center self-start rounded border-0 bg-[#e6e6e6] px-3.5 font-[Helvetica,sans-serif] text-sm font-semibold text-[#2484ff] antialiased',
  preview:
    'relative z-[1] box-border flex min-h-[60px] w-full flex-row items-center justify-between border-b border-[#ececec] px-[3%] py-10',
  previewImage: 'h-auto max-h-10 w-auto max-w-[140px] rounded',
  previewStatusContainer: 'flex items-center',
  previewFileName: 'font-[Helvetica,sans-serif] text-sm font-normal text-[#333333]',
  previewButton:
    'mx-0 mb-0.5 ml-2.5 h-3.5 w-3.5 cursor-pointer bg-center bg-no-repeat opacity-90 [background-size:14px_14px]',
  submitButtonContainer: 'z-[1] my-6',
  submitButton:
    'min-h-8 cursor-pointer rounded border-0 bg-[#2484ff] px-3.5 font-[Helvetica,sans-serif] text-sm font-semibold text-surface antialiased disabled:cursor-unset disabled:bg-[#e6e6e6] disabled:text-[#333333]',
};

export const mergeStyles = (
  classNames: TStyleCustomization<string>,
  styles: TStyleCustomization<React.CSSProperties>,
  addClassNames: TStyleCustomization<string>,
  ...args: unknown[]
) => {
  const resolvedClassNames: {
    [property: string]: string;
  } = { ...defaultClassNames };
  const resolvedStyles = { ...styles } as {
    [property: string]: string;
  };

  for (const [key, value] of Object.entries(classNames)) {
    resolvedClassNames[key] = resolveValue(value, ...args);
  }

  for (const [key, value] of Object.entries(addClassNames)) {
    resolvedClassNames[key] = `${resolvedClassNames[key]} ${resolveValue(value, ...args)}`;
  }

  for (const [key, value] of Object.entries(styles)) {
    resolvedStyles[key] = resolveValue(value, ...args) as string;
  }

  return { classNames: resolvedClassNames, styles: resolvedStyles as TStyleCustomization<React.CSSProperties> };
};

export const getFilesFromEvent = (
  event: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>,
): Array<File | DataTransferItem> => {
  let items = null;

  if ('dataTransfer' in event) {
    const dt = event.dataTransfer;

    // NOTE: Only the 'drop' event has access to DataTransfer.files, otherwise it will always be empty
    if ('files' in dt && dt.files.length) {
      items = dt.files;
    } else if (dt.items?.length) {
      items = dt.items;
    }
  } else if (event.target?.files) {
    items = event.target.files;
  }

  return Array.prototype.slice.call(items);
};
